import { linkOptions } from '@tanstack/react-router';
import type { components } from '@/api/qraft/schema';
import type { TranslationKey } from '~/types/i18n';

export type GlobalSearchHit = components['schemas']['GlobalSearchHit'];
export type GlobalSearchKind = components['schemas']['GlobalSearchKind'];

/**
 * Sections in legacy's registration order, not alphabetical or by count. Legacy
 * also lists blog posts and pages, which stay on WordPress.
 */
export const GLOBAL_SEARCH_KINDS = [
  'courses',
  'lessons',
  'topics',
  'follow-up-tutorials',
] as const satisfies readonly GlobalSearchKind[];

/** The sidebar's own key space: the four kinds plus the All tab. */
export type ResultsFilter = 'all' | GlobalSearchKind;

/**
 * Every one of these is translated in all four locales.
 * `lessons.follow-up-tutorials` is the plural: legacy's tab shows the singular
 * through a swapped argument, and that string is English-only.
 */
export const KIND_LABEL_KEYS = {
  courses: 'navigation.courses',
  lessons: 'wbs.lessons',
  topics: 'wbs.topics',
  'follow-up-tutorials': 'lessons.follow-up-tutorials',
} as const satisfies Record<GlobalSearchKind, TranslationKey>;

/** `?list=` is 1-based, 20 rows a page. */
export const SUBSET_PAGE_SIZE = 20;

/**
 * The All tab shows 3 per section, as legacy's `?view=content` does. It's also
 * the per-kind `limit`, so each response carries the preview rows and the
 * kind's full `count`, and the sidebar badges need no extra request.
 */
export const ALL_PREVIEW = 3;

/** 2 per kind × 4 kinds = the 8 rows the header dropdown shows. */
export const SUGGESTION_LIMIT_PER_KIND = 2;

/** Legacy's autocomplete minimum length. */
export const SUGGESTION_MIN_LENGTH = 2;

function isGlobalSearchKind(value: unknown): value is GlobalSearchKind {
  return (GLOBAL_SEARCH_KINDS as readonly string[]).includes(String(value));
}

export type GlobalSearchParams = {
  s?: string;
  bp_search?: number;
  view?: string;
  subset?: GlobalSearchKind;
  list?: number;
};

/**
 * `?subset=` carries the v3 kind names, not legacy's WordPress post type
 * values; the parameter names stay legacy's.
 *
 * An unknown `subset` falls back to the All tab, where legacy renders a dead
 * end with no tabs.
 */
export function validateGlobalSearchParams(
  search: Record<string, unknown>,
): GlobalSearchParams {
  const list = Number(search.list);

  return {
    // `search.s !== undefined`, not truthiness: an empty `s` must survive,
    // since legacy renders the search page for an empty term, and dropping it
    // loses the parameter on the next `navigate({ search })`.
    s: search.s !== undefined ? String(search.s) : undefined,
    bp_search: search.bp_search ? Number(search.bp_search) : undefined,
    view: search.view ? String(search.view) : undefined,
    subset: isGlobalSearchKind(search.subset) ? search.subset : undefined,
    // Page 1 is the absence of the parameter, as in legacy.
    list: Number.isInteger(list) && list > 1 ? list : undefined,
  };
}

/**
 * Legacy's header search always adds `bp_search=1` and `view=content`, so an
 * empty submit still lands on the search page. Emitted for an empty term too.
 */
export function buildGlobalSearchParams(term: string): GlobalSearchParams {
  return { s: term.trim(), bp_search: 1, view: 'content' };
}

/**
 * The mode is the discriminator, never the term: without `bp_search=1` the same
 * `?s=` URL is WordPress's native search.
 */
export function isGlobalSearchMode(params: GlobalSearchParams): boolean {
  return params.bp_search === 1;
}

/**
 * `{kind, slug}` is everything a link needs: the four destinations are flat
 * permalinks, with `/topic/` singular as in legacy. Built with `linkOptions` so
 * the paths and params are type-checked here once rather than at each call
 * site.
 */
export function hitLinkProps(hit: GlobalSearchHit) {
  switch (hit.kind) {
    case 'courses':
      return linkOptions({
        to: '/courses/$courseSlug/',
        params: { courseSlug: hit.slug },
      });
    case 'lessons':
      return linkOptions({
        to: '/lessons/$lessonSlug/',
        params: { lessonSlug: hit.slug },
      });
    case 'topics':
      return linkOptions({
        to: '/topic/$topicSlug/',
        params: { topicSlug: hit.slug },
      });
    case 'follow-up-tutorials':
      return linkOptions({
        to: '/follow-up-tutorials/$slug/',
        params: { slug: hit.slug },
      });
  }
}
